import axios from "axios";

const BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const api = axios.create({
  baseURL: BASE,
  timeout: 20000,
});

export const absUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${process.env.REACT_APP_BACKEND_URL}${path}`;
};

// Force a proper landscape crop on Unsplash / imgix photos so tall source
// images don't get squashed into landscape article cards.
export const cropUnsplash = (url, { w = 1200, h = 800 } = {}) => {
  if (!url || !/images\.unsplash\.com/i.test(url)) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    u.searchParams.set("h", String(h));
    u.searchParams.set("fit", "crop");
    u.searchParams.set("crop", "entropy");
    u.searchParams.set("cs", "srgb");
    u.searchParams.set("fm", "jpg");
    u.searchParams.set("q", "80");
    u.searchParams.set("auto", "format");
    return u.toString();
  } catch {
    return url;
  }
};

// Retry wrapper — up to 6 attempts with progressive backoff (covers up to ~10s of backend restart).
const withRetry = async (fn, attempts = 6, baseDelayMs = 400) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        // Progressive backoff: 400, 800, 1200, 1800, 2600, ...
        const delay = baseDelayMs * (1 + i) * (1 + i * 0.3);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastErr;
};

export const ArticlesAPI = {
  list: (params = {}) =>
    withRetry(() => api.get("/articles", { params })).then((r) => r.data),
  get: (slug) =>
    withRetry(() => api.get(`/articles/${slug}`)).then((r) => r.data),
  create: (formData) =>
    api
      .post("/articles", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  comingSoon: (limit = 12) =>
    withRetry(() => api.get("/articles/coming-soon", { params: { limit } }))
      .then((r) => r.data),
};

export const SubscribeAPI = {
  add: (email, tier = "free") =>
    api.post("/subscribe", { email, tier }).then((r) => r.data),
};

export const WireAPI = {
  list: (limit = 12) =>
    withRetry(() => api.get("/wire", { params: { limit } })).then((r) => r.data),
};

export const VexGlobalLinksAPI = {
  latest: () =>
    withRetry(() => api.get("/vexgloballinks/latest")).then((r) => r.data),
  countries: () =>
    withRetry(() => api.get("/vexgloballinks/countries")).then((r) => r.data),
};

export const ArchiveAPI = {
  list: (params = {}) =>
    withRetry(() => api.get("/archive", { params })).then((r) => r.data),
};

export const AudioAPI = {
  generate: (articleId, voice) =>
    api
      .post(`/admin/articles/${articleId}/generate-audio`, voice ? { voice } : {})
      .then((r) => r.data),
  status: (articleId) =>
    api.get(`/admin/articles/${articleId}/audio-status`).then((r) => r.data),
  remove: (articleId) =>
    api.delete(`/admin/articles/${articleId}/audio`).then((r) => r.data),
  healthOne: (articleId) =>
    api.get(`/admin/articles/${articleId}/audio-health`).then((r) => r.data),
  healthAll: () =>
    api.get(`/admin/audio/health-all`).then((r) => r.data),
  stats: (premiumThreshold = 200, windowDays = 7) =>
    api
      .get(`/admin/audio/stats`, {
        params: { premium_threshold: premiumThreshold, window_days: windowDays },
      })
      .then((r) => r.data),
  play: (articleId) =>
    api.post(`/articles/${articleId}/audio-play`).then((r) => r.data),
  voices: () => api.get(`/admin/audio/voices`).then((r) => r.data),
  previewUrl: (voice) => {
    // Direct URL an <audio> element can consume; no auth needed inside admin
    const base = api.defaults.baseURL || "";
    return `${base}/admin/audio/voice-preview?voice=${encodeURIComponent(voice)}`;
  },
};

export const ArticleStatsAPI = {
  view: (articleId) => api.post(`/articles/${articleId}/view`).catch(() => null),
};

export const CommentsAPI = {
  list: (articleId) =>
    api.get(`/articles/${articleId}/comments`).then((r) => r.data),
  create: (articleId, name, body) =>
    api.post(`/articles/${articleId}/comments`, { name, body }).then((r) => r.data),
  remove: (id) => api.delete(`/admin/comments/${id}`).then((r) => r.data),
};

export const NewsletterAPI = {
  subscribe: (email, source) =>
    api.post(`/newsletter/subscribe`, { email, source }).then((r) => r.data),
};

export const ContentControlsAPI = {
  updateMeta: (articleId, patch) =>
    api.patch(`/admin/articles/${articleId}/meta`, patch).then((r) => r.data),
  bump: (articleId) =>
    api.post(`/admin/articles/${articleId}/bump`).then((r) => r.data),
  archiveNow: (articleId) =>
    api.post(`/admin/articles/${articleId}/archive-now`).then((r) => r.data),
  listArchived: () =>
    api.get(`/admin/archive/list`).then((r) => r.data),
  restore: (archiveId, bumpToNow = true) =>
    api.post(`/admin/archive/restore/${archiveId}`, { bumpToNow }).then((r) => r.data),
};

// Vexent Wire taxonomy — heuristic derivation when a piece is missing an
// explicit `vexCategory`. Priority order:
//   1. explicit override from the article doc
//   2. "alert" / "breaking" signal in kicker or tags → VexAlert
//   3. long-form professional research → VexDeep
//   4. chart/map/data tags → VexView
//   5. "brief" / "morning" / "summary" tags → VexBrief
//   6. default → VexNews
export const deriveVexCategory = (a) => {
  if (!a) return "VexNews";
  if (a.vexCategory) return a.vexCategory;
  const kicker = (a.kicker || a.category || "").toLowerCase();
  const tags = (a.tags || []).map((t) => String(t).toLowerCase());
  const title = (a.title || "").toLowerCase();
  const bodyLen = (a.body || "").length;
  const isAlert =
    kicker.includes("alert") ||
    kicker.includes("breaking") ||
    tags.some((t) => t.includes("alert") || t.includes("breaking"));
  if (isAlert) return "VexAlert";
  if (
    tags.some((t) => t.includes("chart") || t.includes("map") || t.includes("data") || t.includes("visual"))
  ) return "VexView";
  if (
    kicker.includes("brief") ||
    kicker.includes("morning") ||
    kicker.includes("ledger") ||
    tags.some((t) => t.includes("brief") || t.includes("summary")) ||
    title.startsWith("morning ")
  ) return "VexBrief";
  if (a.readTier === "professional" || bodyLen > 6000) return "VexDeep";
  return "VexNews";
};

export const normalizeArticle = (a) => {
  if (!a) return a;
  return {
    ...a,
    image: absUrl(a.image),
    audio: absUrl(a.audio),
    video: absUrl(a.video),
    bodyFile: absUrl(a.bodyFile),
    podcastAudio: absUrl(a.podcastAudio),
    // Attach a resolved category so downstream components can render
    // the taxonomy badge without repeating the derivation.
    vexCategory: deriveVexCategory(a),
  };
};
